import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { getApprovalDetail, respondToApproval } from "../../api/agentApi";

interface InputField {
  name: string;
  type: "text" | "textarea" | "select" | "boolean" | "number";
  label?: string;
  required?: boolean;
  default_value?: any;
  options?: string[];
  description?: string;
}

interface ApprovalDetail {
  id: string;
  workflow_run_id: string;
  step_id: string;
  title: string;
  prompt: string;
  display_data: any;
  input_fields: InputField[] | null;
  status: string;
  response_data: any;
  responded_by: string | null;
  responded_at: string | null;
  created_at: string | null;
  expires_at: string | null;
}

export default function ApprovalDetailPage() {
  const { approvalId } = useParams<{ approvalId: string }>();
  const navigate = useNavigate();
  const [approval, setApproval] = useState<ApprovalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});

  const fetchApproval = useCallback(async () => {
    if (!approvalId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getApprovalDetail(approvalId);
      setApproval(data);

      // Initialize field values with defaults
      const defaults: Record<string, any> = {};
      if (data.input_fields) {
        data.input_fields.forEach((field: InputField) => {
          defaults[field.name] =
            field.default_value !== undefined
              ? field.default_value
              : field.type === "boolean"
              ? false
              : "";
        });
      }
      setFieldValues(defaults);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [approvalId]);

  useEffect(() => {
    fetchApproval();
  }, [fetchApproval]);

  const handleFieldChange = (name: string, value: any) => {
    setFieldValues((prev) => ({ ...prev, [name]: value }));
  };

  const hasFormFields =
    approval !== null &&
    Array.isArray(approval.input_fields) &&
    approval.input_fields.length > 0;

  const isFormValid = () => {
    if (!approval?.input_fields) return true;
    return approval.input_fields
      .filter((f) => f.required)
      .every((f) => {
        const val = fieldValues[f.name];
        return val !== undefined && val !== "" && val !== null;
      });
  };

  // Used when input_fields are present — reads decision from the form values.
  const handleFormSubmit = async () => {
    if (!approvalId) return;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const response = await respondToApproval(approvalId, fieldValues);
      const decision = response?.decision ?? fieldValues.decision;
      setSubmitResult({
        type: "success",
        message: decision
          ? `Decision "${decision}" submitted successfully. The workflow will resume.`
          : "Response submitted successfully. The workflow will resume.",
      });
      fetchApproval();
    } catch (err) {
      setSubmitResult({
        type: "error",
        message: (err as Error).message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Legacy: used when no input_fields are defined — decision comes from the button clicked.
  const handleSubmit = async (decision: "approve" | "reject") => {
    if (!approvalId) return;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const response = await respondToApproval(approvalId, { decision, ...fieldValues });
      const actualDecision = response?.decision ?? decision;
      setSubmitResult({
        type: "success",
        message: `Approval ${actualDecision === "approve" ? "approved" : "rejected"} successfully. The workflow will resume.`,
      });
      fetchApproval();
    } catch (err) {
      setSubmitResult({
        type: "error",
        message: (err as Error).message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isExpired =
    approval?.expires_at && new Date(approval.expires_at) < new Date();
  const isPending = approval?.status === "pending" && !isExpired;

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!approval) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Approval not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: 3, maxWidth: 800, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/workflow/approvals")}
          sx={{ textTransform: "none", mb: 2, color: "#64748B" }}
        >
          Back to Approvals
        </Button>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {approval.title || "Approval Required"}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Step: {approval.step_id} | Run: {approval.workflow_run_id}
            </Typography>
          </Box>
          <Chip
            label={
              isExpired
                ? "Expired"
                : approval.status === "pending"
                ? "Pending"
                : approval.status
            }
            color={
              isExpired
                ? "error"
                : approval.status === "pending"
                ? "warning"
                : approval.status === "approved"
                ? "success"
                : "error"
            }
            variant="outlined"
          />
        </Box>
      </Box>

      {submitResult && (
        <Alert
          severity={submitResult.type}
          sx={{ mb: 2 }}
          onClose={() => setSubmitResult(null)}
        >
          {submitResult.message}
        </Alert>
      )}

      {/* Prompt */}
      {approval.prompt && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
            bgcolor: "#F8FAFC",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: "#475569", mb: 1 }}
          >
            Instructions
          </Typography>
          <Typography sx={{ fontSize: "14px", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {approval.prompt}
          </Typography>
        </Paper>
      )}

      {/* Display Data */}
      {approval.display_data &&
        Object.keys(approval.display_data).length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "#475569", mb: 1.5 }}
            >
              Data for Review
            </Typography>
            <Box
              sx={{
                bgcolor: "#F8FAFC",
                p: 2,
                borderRadius: "6px",
                border: "1px solid #E2E8F0",
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontSize: "13px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "monospace",
                }}
              >
                {typeof approval.display_data === "string"
                  ? approval.display_data
                  : JSON.stringify(approval.display_data, null, 2)}
              </pre>
            </Box>
          </Paper>
        )}

      {/* Input Fields Form */}
      {isPending && approval.input_fields && approval.input_fields.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: "#475569", mb: 2 }}
          >
            Your Response
          </Typography>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {approval.input_fields.map((field) => (
              <Box key={field.name}>
                {field.type === "text" && (
                  <TextField
                    fullWidth
                    size="small"
                    label={
                      field.label ||
                      field.name + (field.required ? " *" : "")
                    }
                    value={fieldValues[field.name] || ""}
                    onChange={(e) =>
                      handleFieldChange(field.name, e.target.value)
                    }
                    helperText={field.description}
                  />
                )}
                {field.type === "textarea" && (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    label={
                      field.label ||
                      field.name + (field.required ? " *" : "")
                    }
                    value={fieldValues[field.name] || ""}
                    onChange={(e) =>
                      handleFieldChange(field.name, e.target.value)
                    }
                    helperText={field.description}
                  />
                )}
                {field.type === "select" && (
                  <FormControl fullWidth size="small">
                    <InputLabel>
                      {field.label ||
                        field.name + (field.required ? " *" : "")}
                    </InputLabel>
                    <Select
                      value={fieldValues[field.name] || ""}
                      label={field.label || field.name}
                      onChange={(e) =>
                        handleFieldChange(field.name, e.target.value)
                      }
                    >
                      {(field.options || []).map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {field.type === "boolean" && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!fieldValues[field.name]}
                        onChange={(e) =>
                          handleFieldChange(field.name, e.target.checked)
                        }
                      />
                    }
                    label={
                      field.label ||
                      field.name + (field.required ? " *" : "")
                    }
                  />
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Already responded */}
      {approval.status !== "pending" && approval.response_data && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: "#475569", mb: 1 }}
          >
            Response
          </Typography>
          <pre
            style={{
              margin: 0,
              fontSize: "13px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              backgroundColor: "#F8FAFC",
              padding: "12px",
              borderRadius: "6px",
            }}
          >
            {JSON.stringify(approval.response_data, null, 2)}
          </pre>
          {approval.responded_at && (
            <Typography
              variant="caption"
              sx={{ color: "#94A3B8", mt: 1, display: "block" }}
            >
              Responded at:{" "}
              {new Date(approval.responded_at).toLocaleString()}
            </Typography>
          )}
        </Paper>
      )}

      {/* Action Buttons */}
      {isPending && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            {hasFormFields ? (
              // When the step defines input_fields (e.g. decision dropdown + comments),
              // a single Submit button is all that's needed — the decision comes from the form.
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={handleFormSubmit}
                disabled={submitting || !isFormValid()}
                sx={{ textTransform: "none", borderRadius: "99px", px: 4 }}
              >
                {submitting ? "Submitting..." : "Submit Decision"}
              </Button>
            ) : (
              // Legacy: no input_fields defined — show explicit Approve / Reject buttons.
              <>
                <Button
                  variant="outlined"
                  color="error"
                  size="large"
                  startIcon={<CancelOutlinedIcon />}
                  onClick={() => handleSubmit("reject")}
                  disabled={submitting}
                  sx={{ textTransform: "none", borderRadius: "99px", px: 3 }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={() => handleSubmit("approve")}
                  disabled={submitting}
                  sx={{ textTransform: "none", borderRadius: "99px", px: 3 }}
                >
                  {submitting ? "Submitting..." : "Approve"}
                </Button>
              </>
            )}
          </Box>
        </>
      )}

      {/* Metadata */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mt: 3,
          border: "1px solid #F1F5F9",
          borderRadius: "8px",
          bgcolor: "#FAFAFA",
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "#94A3B8", display: "block" }}
        >
          Approval ID: {approval.id}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "#94A3B8", display: "block" }}
        >
          Created:{" "}
          {approval.created_at
            ? new Date(approval.created_at).toLocaleString()
            : "—"}
        </Typography>
        {approval.expires_at && (
          <Typography
            variant="caption"
            sx={{
              color: isExpired ? "#EF4444" : "#94A3B8",
              display: "block",
            }}
          >
            Expires:{" "}
            {new Date(approval.expires_at).toLocaleString()}
            {isExpired ? " (EXPIRED)" : ""}
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
